<script setup>
import { onMounted, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import conversationStatusesAPI from 'dashboard/api/conversationStatuses';
import serviceTemplatesAPI from 'dashboard/api/serviceTemplates';

const statuses = ref([]);
const templates = ref([]);
const statusName = ref('');
const statusColor = ref('#3B82F6');
const templateName = ref('');
const templateDescription = ref('');
const templateItems = ref('');
const isSavingStatus = ref(false);
const isSavingTemplate = ref(false);

const load = async () => {
  try {
    const [statusResponse, templateResponse] = await Promise.all([
      conversationStatusesAPI.get(),
      serviceTemplatesAPI.get(),
    ]);
    statuses.value = statusResponse.data.payload || [];
    templates.value = templateResponse.data.payload || [];
  } catch {
    useAlert('Não foi possível carregar as configurações de atendimento.');
  }
};

const addStatus = async () => {
  if (!statusName.value.trim()) return;
  isSavingStatus.value = true;
  try {
    await conversationStatusesAPI.create({
      conversation_status: {
        name: statusName.value.trim(),
        color: statusColor.value,
        position: statuses.value.length,
      },
    });
    statusName.value = '';
    await load();
    useAlert('Status cadastrado com sucesso.');
  } catch {
    useAlert(
      'Não foi possível cadastrar o status. Verifique se o nome já existe.'
    );
  } finally {
    isSavingStatus.value = false;
  }
};

const removeStatus = async status => {
  try {
    await conversationStatusesAPI.delete(status.id);
    await load();
    useAlert('Status removido. As conversas mantiveram todo o histórico.');
  } catch {
    useAlert('Não foi possível remover o status.');
  }
};

const toggleStatus = async status => {
  try {
    await conversationStatusesAPI.update(status.id, {
      conversation_status: { active: !status.active },
    });
    await load();
  } catch {
    useAlert('Não foi possível atualizar o status.');
  }
};

const addTemplate = async () => {
  const items = templateItems.value
    .split('\n')
    .map(title => title.trim())
    .filter(Boolean)
    .map(title => ({ title, required: true }));
  if (!templateName.value.trim() || !items.length) {
    useAlert('Informe o nome e pelo menos uma etapa do atendimento.');
    return;
  }
  isSavingTemplate.value = true;
  try {
    await serviceTemplatesAPI.create({
      service_template: {
        name: templateName.value.trim(),
        description: templateDescription.value.trim(),
        items,
      },
    });
    templateName.value = '';
    templateDescription.value = '';
    templateItems.value = '';
    await load();
    useAlert('Modelo de atendimento cadastrado.');
  } catch {
    useAlert('Não foi possível cadastrar o modelo de atendimento.');
  } finally {
    isSavingTemplate.value = false;
  }
};

const removeTemplate = async template => {
  try {
    await serviceTemplatesAPI.delete(template.id);
    await load();
    useAlert('Modelo removido. Checklists já vinculados foram preservados.');
  } catch {
    useAlert('Não foi possível remover o modelo. Talvez ele esteja em uso.');
  }
};

onMounted(load);
</script>

<template>
  <div class="grid gap-6 lg:grid-cols-2">
    <section
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
    >
      <header class="px-5 py-4 border-b border-n-weak">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('OPERATIONAL_WORKFLOW.STATUS.TITLE') }}
        </h3>
        <p class="mt-1 mb-0 text-body-para text-n-slate-11">
          {{ $t('OPERATIONAL_WORKFLOW.STATUS.DESCRIPTION') }}
        </p>
      </header>
      <form class="flex gap-2 p-5" @submit.prevent="addStatus">
        <input
          v-model="statusColor"
          type="color"
          :aria-label="$t('OPERATIONAL_WORKFLOW.STATUS.COLOR')"
          class="size-10 rounded-lg border border-n-weak bg-transparent"
        />
        <input
          v-model="statusName"
          class="flex-1 h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="$t('OPERATIONAL_WORKFLOW.STATUS.PLACEHOLDER')"
        />
        <Button
          type="submit"
          icon="i-lucide-plus"
          :label="$t('OPERATIONAL_WORKFLOW.STATUS.ADD')"
          :is-loading="isSavingStatus"
        />
      </form>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="status in statuses"
          :key="status.id"
          class="flex items-center gap-3 px-5 py-3"
        >
          <span
            class="size-3 rounded-full"
            :style="{ backgroundColor: status.color }"
          />
          <span
            class="flex-1 text-n-slate-12"
            :class="{ 'opacity-50': !status.active }"
          >
            {{ status.name }}
          </span>
          <Button
            slate
            sm
            :label="
              status.active
                ? $t('OPERATIONAL_WORKFLOW.STATUS.DISABLE')
                : $t('OPERATIONAL_WORKFLOW.STATUS.ENABLE')
            "
            @click="toggleStatus(status)"
          />
          <Button
            slate
            sm
            icon="i-lucide-trash-2"
            @click="removeStatus(status)"
          />
        </div>
        <p v-if="!statuses.length" class="px-5 py-4 text-n-slate-11">
          {{ $t('OPERATIONAL_WORKFLOW.STATUS.EMPTY') }}
        </p>
      </div>
    </section>

    <section
      class="rounded-xl outline outline-1 outline-n-container bg-n-solid-2"
    >
      <header class="px-5 py-4 border-b border-n-weak">
        <h3 class="text-heading-2 text-n-slate-12">
          {{ $t('OPERATIONAL_WORKFLOW.TEMPLATE.TITLE') }}
        </h3>
        <p class="mt-1 mb-0 text-body-para text-n-slate-11">
          {{ $t('OPERATIONAL_WORKFLOW.TEMPLATE.DESCRIPTION') }}
        </p>
      </header>
      <form class="grid gap-3 p-5" @submit.prevent="addTemplate">
        <input
          v-model="templateName"
          class="h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="$t('OPERATIONAL_WORKFLOW.TEMPLATE.NAME')"
        />
        <input
          v-model="templateDescription"
          class="h-10 px-3 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="
            $t('OPERATIONAL_WORKFLOW.TEMPLATE.OPTIONAL_DESCRIPTION')
          "
        />
        <textarea
          v-model="templateItems"
          rows="4"
          class="px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          :placeholder="$t('OPERATIONAL_WORKFLOW.TEMPLATE.ITEMS')"
        />
        <Button
          type="submit"
          icon="i-lucide-plus"
          :label="$t('OPERATIONAL_WORKFLOW.TEMPLATE.ADD')"
          :is-loading="isSavingTemplate"
        />
      </form>
      <div class="divide-y divide-n-weak border-t border-n-weak">
        <div
          v-for="template in templates"
          :key="template.id"
          class="flex items-start gap-3 px-5 py-3"
        >
          <div class="flex-1">
            <p class="mb-0 font-medium text-n-slate-12">{{ template.name }}</p>
            <p class="mb-0 text-xs text-n-slate-11">
              {{
                $t('OPERATIONAL_WORKFLOW.TEMPLATE.ITEM_COUNT', {
                  count: template.items.length,
                })
              }}
            </p>
          </div>
          <Button
            slate
            sm
            icon="i-lucide-trash-2"
            @click="removeTemplate(template)"
          />
        </div>
        <p v-if="!templates.length" class="px-5 py-4 text-n-slate-11">
          {{ $t('OPERATIONAL_WORKFLOW.TEMPLATE.EMPTY') }}
        </p>
      </div>
    </section>
  </div>
</template>
